import env from 'env'

import { AmplitudeClient, Callback, Config, Identify } from 'amplitude-js'
import { load, toFixed } from '../../../utils'

import logger from '../../../utils/logger'

import { ReleaseProduct } from '../../../types'
import { SourceData } from '../../telemetry/types'

import { AMPLITUDE_INSTANCE_KEY, AMPLITUDE_LIBRARY_URL } from '../../../constants'
import { AMPLITUDE_USER_CONTENT_PLAYED, AMPLITUDE_USER_PLAYBACK_INITIATED, APPLICATION_STATE, CONTENT_TYPE, STOP_REASON } from '.'
import { format, parseISO } from 'date-fns'
import { ACCESS_TYPE } from '../../telemetry/enums'
import { CONTENT_SUBTYPE } from '../../../constants/enums'

type PlainObject = Record<string, unknown>

const options = {
  includeGclid: true,
  includeUtm: true,
  includeReferrer: true,
  logAttributionCapturedEvent: true
} as Config

const {
  AMPLITUDE_API_KEY
} = env

type AmplitudeLibrary = {
  init(apiKey: string, userId?: string, config?: Config, callback?: (client: AmplitudeClient) => void): void,
  getInstance(instanceName?: string): AmplitudeClient
  getSessionId: () => number,
  setUserId: (userId: string | null) => AmplitudeClient,
  identify: (identify: Identify) => AmplitudeClient,
  logEventWithTimestamp<T>(event: string, data?: T, timestamp?: number, callback?: Callback, ): void
}

export class AmplitudeAnalyticsCore {
  #enabled = true
  #amplitude!: AmplitudeLibrary

  get sessionId(): number | undefined {
    return this.amplitude?.getInstance()?.getSessionId()
  }

  get logger() {
    return logger
  }

  get name(): string {
    return 'Amplitude'
  }

  get amplitude() {
    return this.#amplitude
  }

  set userId(userId: string | null) {
    this.amplitude?.getInstance()?.setUserId(userId)
  }

  set enabled(enabled: boolean) {
    this.#enabled = enabled
  }

  async init(userId?: string | undefined, key?: string) {
    if (!this.#amplitude) {
      const global = window as unknown as { [key: string]: unknown }
      // Preserve old amplitude instance
      const oldAmplitude = window.amplitude ? Object.assign(Object.create(Object.getPrototypeOf(window.amplitude)), window.amplitude) : undefined

      // Handle case when amplitude is already loaded
      window.amplitude = undefined as never

      // Load new version of amplitude
      await load(AMPLITUDE_LIBRARY_URL, key ?? AMPLITUDE_INSTANCE_KEY)

      // Preserve
      this.#amplitude = Object.assign(Object.create(Object.getPrototypeOf(window.amplitude)), window.amplitude) as unknown as AmplitudeLibrary

      // Set global amplitude instance
      if (key) global[key] = this.#amplitude

      // Restore old amplitude instance
      if (oldAmplitude) window.amplitude = oldAmplitude

      this.logger.ORANGE.OUTLINE('Amplitude', 'init', this.amplitude)
    }

    return new Promise<AmplitudeClient>(resolve => {
      this.#amplitude.init(AMPLITUDE_API_KEY, userId, options, resolve)
    })
  }

  async identify(identify: Identify): Promise<void | AmplitudeClient> {
    this.amplitude?.getInstance()?.identify(identify)
  }


  async logEvent(event: string, data?: PlainObject, timestamp: number = Date.now()): Promise<void | AmplitudeClient> {
    return new Promise<void | AmplitudeClient>(resolve => {
      if (!this.#enabled || !this.amplitude) return resolve()

      try {
        const payload = { ...(data || {}) }

        this.logger.ORANGE.OUTLINE(this.name, `[${event}]`, payload)

        this.amplitude.getInstance()?.logEventWithTimestamp(event, payload, timestamp, resolve as never)
      }
      catch (error) {
        this.logger.ERROR(`${this.name}:[send]`, error)
        resolve()
      }
    })
  }
}

export class AmplitudeAnalytics extends AmplitudeAnalyticsCore {
  logVideoPlaybackInit(release: ReleaseProduct, source: SourceData) {
    const event = {
      'artist id': release.artist?.id,
      'artist name': release.artist?.name,
      'content type': CONTENT_TYPE.VIDEO,
      'content subtype': source.contentSubtype,
      page: source.page,
      section: source.section,
      'show id': release.id,
      'show name': release.name
    }

    this.logEvent(AMPLITUDE_USER_PLAYBACK_INITIATED, event)
  }

  logPlaybackEnded(
    contentType: CONTENT_TYPE,
    release: ReleaseProduct,
    source: SourceData,
    type: ACCESS_TYPE | null,
    duration: number,
    reason: STOP_REASON,
    trackName?: string,
    trackId?: string | number,
    trackQuality?: string) {
    if (!release || !source) return

    const date = release.show?.performancedAt || release.album?.releasedAt || release.stream?.startsAt

    const contentSubtype = contentType == CONTENT_TYPE.AUDIO ?
      (release.type == CONTENT_SUBTYPE.ALBUM ? CONTENT_SUBTYPE.ALBUM : source.contentSubtype) :
      source.contentSubtype == CONTENT_SUBTYPE.SHOW ? CONTENT_SUBTYPE.SVOD : source.contentSubtype

    const none = '(none)'

    const event = {
      'url': window.location.href,
      'origin': window.location.origin,
      'application state': APPLICATION_STATE.FOREGROUND,
      'artist id': release.artist?.id,
      'artist name': release.artist?.name,
      'business model': type,
      city: release.venue?.city,
      'content type': contentType,
      'content subtype': contentSubtype,
      duration: toFixed(duration),
      'performance date': date ? format(parseISO((date) as never), 'MMM dd, yyyy') : '',
      'show id': release.show.id,
      'show name': release.show.title,
      state: release.venue?.state,
      'stop reason': reason,
      'is live': contentType === CONTENT_TYPE.AUDIO ? false : release.stream.isLive,
      venue: release.venue?.name,
      'track id': contentType === CONTENT_TYPE.AUDIO ? trackId : none,
      'track name': trackName ?? none,
      'quality': contentType === CONTENT_TYPE.AUDIO ? trackQuality?.toLowerCase() : none,
    }

    this.logEvent(AMPLITUDE_USER_CONTENT_PLAYED, event)
  }
}

export default new AmplitudeAnalytics
